import { FC, useCallback, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { useMessage, useToastNotification } from '@/hooks';
import { useLocation, useNavigate } from 'react-router-dom';

import { Dialog } from 'primereact/dialog';
import { Nullable } from 'primereact/ts-helpers';
import { Messages } from 'primereact/messages';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faCalculator } from '@fortawesome/free-solid-svg-icons';

import {
  InputDateField,
  InputTextField,
  Button,
  LoadingSpinner,
} from '@/components';
import CheckBox from '@/components/CheckBox/CheckBox';
import { queryClient } from '@/providers/AppProvider';
import { formatDateAndTime } from '@/utils/formatDate';

import { getLiquidateContainerBlExport } from '../Depo20/services/getLiquidateContainerBlExport';
import { getOidVisita } from './services/getOidVisita';
import { estaEnLiquidacionProforma } from './services/estaEnLiquidacionProforma';

import InvoiceDetail from './InvoiceDetail';
import ModalDeleted from './ModalDeleted';

import styles from './AddContainerModalImportFCL.module.scss';
import { addContainerBookingExport } from '../Depo20/services/addContainerBookingExport';
import { getValidatedContainerBooking } from '../Depo20/services/getValidatedContainerBooking';
import { necesitaPreguntarPorEarlyArrival } from './services/necesitaPreguntarPorEarlyArrival';
import {
  saveAndAddSettlement,
  SaveSettlementValidated,
} from '../Depo20/services/saveAndAddSettlement';
import { validateContainerExport } from '../Depo20/services/validateContainerExport';

type ModalProps = {
  setVisibility: (visibility: boolean | ((prev: boolean) => boolean)) => void;
  visible: boolean;
  oidLiquidacion: number;
  bodyOnSave: SaveSettlementValidated;
  rut: string;
};

const regexValidate = /^[A-Za-z]{4}\d{7}$/;

export const AddContainerModalExportFCL: FC<ModalProps> = ({
  setVisibility,
  visible,
  oidLiquidacion,
  bodyOnSave,
  rut,
}) => {
  const { t } = useTranslation(['importFCL', 'exportFCL']);
  const { showToast } = useToastNotification();
  const { showMessage, elementRef, clearMessages } = useMessage();
  const [isLateArrival, setIsLateArrival] = useState(false);
  const [deleteModalIsVisible, setDeleteModalIsVisible] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const showMessageError = useCallback(
    (message: string, title?: string) => {
      showMessage({
        severity: 'error',
        detail: message,
        summary: title,
      });
    },
    [showMessage]
  );

  const [formValue, setFormValue] = useState({
    contenedor: '',
    fechaIngreso: null,
    lateArrival: false,
  });

  const toggleDialog = useCallback(() => {
    setVisibility((isVisible: boolean) => !isVisible);
  }, [setVisibility]);

  const calculateAmount = useCallback(
    async (data: { oidVisita: number }) => {
      const { lateArrival, fechaIngreso } = formValue;

      const body = {
        oidVisita: data?.oidVisita || 0,
        fecha: fechaIngreso || '',
        ingresoTardio: lateArrival,
        rut,
      };

      return getLiquidateContainerBlExport(body);
    },
    [formValue, rut]
  );

  const validateContainer = useCallback(
    async (data: { oidVisita: number }) => {
      const body = {
        oidVisita: data?.oidVisita,
        fecha: formValue?.fechaIngreso || '',
      };

      const validate = await validateContainerExport(body);
      if (validate?.ok === false) {
        throw new Error(validate?.mensaje);
      } else {
        clearMessages();
      }

      const validateBooking = await getValidatedContainerBooking(body);
      if (validateBooking?.ok === false) {
        throw new Error(validateBooking?.mensaje);
      } else {
        clearMessages();
      }

      const isLate = await necesitaPreguntarPorEarlyArrival(body);
      setIsLateArrival(isLate);
      if (!isLate) {
        setFormValue((currentValue) => ({
          ...currentValue,
          lateArrival: false,
        }));
      }

      return true;
    },
    [formValue, clearMessages]
  );

  const {
    mutate: validateContainerMutation,
    data: isValid,
    isPending: validatingContainer,
  } = useMutation({
    mutationFn: validateContainer,
    onError: (error) => {
      clearMessages();
      showMessageError(error?.message);
    },
  });

  const {
    mutate: getContainerData,
    data: containerData,
    isPending: gettingContainerData,
  } = useMutation({
    mutationFn: getOidVisita,
    onError: (error) => {
      clearMessages();
      showMessageError(error?.message);
    },
    onSuccess: (data) => validateContainerMutation(data),
  });

  const {
    mutate: calculateAmountMutation,
    data: amountData,
    isSuccess,
    isPending: calculatingAmount,
  } = useMutation({
    mutationFn: calculateAmount,
    onError: () => {
      clearMessages();
      showMessageError(t('importFCL:errorCalculatingAmount'));
    },
  });

  const {
    mutate: checkIsOnSettlement,
    isPending: isCheckingAlreadyOnSettlement,
  } = useMutation({
    mutationFn: estaEnLiquidacionProforma,
    onSuccess: (isUsed) => {
      if (!isUsed && containerData) {
        return calculateAmountMutation(containerData);
      }
      return setDeleteModalIsVisible(true);
    },
    onError: (error) => {
      clearMessages();
      showMessageError(error?.message);
    },
  });
  const { contenedor, fechaIngreso, lateArrival } = formValue;

  const { mutate: addContainer, isPending: addingContainer } = useMutation({
    mutationFn: async (currentOidLiquidacion: number) => {
      if (!containerData) return;
      const { oidVisita } = containerData;

      const body = {
        oidVisita,
        fechaIngreso: fechaIngreso || '',
        lateArrival,
        oidLiquidacion: currentOidLiquidacion,
      };
      await addContainerBookingExport(body);
    },
    onError: (error) => {
      clearMessages();

      showToast({
        severity: 'error',
        detail: error?.message ?? t('importFCL:errorAddingContainers'),
      });

      showMessageError(error?.message ?? t('importFCL:errorAddingContainers'));
    },
    onSuccess: () => {
      clearMessages();
      showToast({
        severity: 'success',
        detail: t('importFCL:containerAdded'),
      });
      queryClient.invalidateQueries({
        queryKey: ['getImportFCLTable'],
      });
      toggleDialog();
    },
  });

  const { mutateAsync: saveData, isPending: savingSettlement } = useMutation({
    mutationFn: saveAndAddSettlement,
  });

  useEffect(() => {
    if (!contenedor || !fechaIngreso) return;
    if (!regexValidate.test(contenedor)) return;

    const marca = contenedor.slice(0, 4);
    const numero = contenedor.slice(4, 11);

    getContainerData({ marca, numero });
  }, [contenedor, fechaIngreso, getContainerData]);

  const handleFieldChange = useCallback(
    (value: string | Nullable<Date> | boolean, name: string) => {
      setFormValue((currentValue) => ({
        ...currentValue,
        [name]: value,
      }));
    },
    []
  );

  const onCalculateButtonClick = useCallback(() => {
    if (!containerData) return;
    checkIsOnSettlement(containerData);
  }, [checkIsOnSettlement, containerData]);

  const onContainerInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let { value } = e.target;
      value = value?.replace(/[^A-Za-z0-9]/g, '');

      if (value?.length > 11) return;

      const marca = value.slice(0, 4);
      const numero = value.slice(4, 11);

      if (marca && !marca.match(/^[A-Za-z]+$/)) return;
      if (numero && !numero.match(/^[0-9]+$/)) return;

      setFormValue((currentValue) => ({
        ...currentValue,
        contenedor: value?.toUpperCase(),
      }));
    },
    []
  );

  const onAddButtonClick = useCallback(async () => {
    if (!containerData) return;

    if (!oidLiquidacion) {
      try {
        const settlementId = await saveData(bodyOnSave);
        addContainer(settlementId);
        const newPath = `${location.pathname}?id=${settlementId}`;
        navigate(newPath, { replace: true });
        showToast({
          severity: 'success',
          detail: t('importFCL:settlementSaved'),
        });
      } catch (error) {
        showToast({
          severity: 'error',
          detail: t('importFCL:settlementNotSaved'),
        });
      }
      return;
    }

    addContainer(oidLiquidacion);
  }, [
    containerData,
    bodyOnSave,
    saveData,
    addContainer,
    oidLiquidacion,
    navigate,
    showToast,
    t,
    location.pathname,
  ]);

  const onDeleteModalClose = (deleted: boolean) => {
    setDeleteModalIsVisible(false);
    if (!deleted || !containerData) return;
    calculateAmountMutation(containerData);
  };

  const cutOffDate = useMemo(() => {
    const { cutoff } = containerData || {};
    if (!cutoff) return '';
    const date = formatDateAndTime(cutoff);
    return date;
  }, [containerData]);

  const isLoading =
    isCheckingAlreadyOnSettlement ||
    addingContainer ||
    calculatingAmount ||
    savingSettlement ||
    validatingContainer;

  return (
    <Dialog
      header={
        <div className="text-blue-800">
          <FontAwesomeIcon icon={faPlus} style={{ marginRight: '0.5rem' }} />
          {t('importFCL:addContainer')}
        </div>
      }
      visible={visible}
      style={{
        maxWidth: window.innerWidth <= 1200 ? '95vw' : '1200px',
        minWidth: window.innerWidth <= 1200 ? '95vw' : '1200px',
        position: 'relative',
      }}
      onHide={toggleDialog}
      modal
      footer={
        <div className={`p-2 md:p-4 ${styles.contianerButtons}`}>
          <Messages ref={elementRef} />
          <div className="w-ful flex justify-content-end">
            <Button text onClick={toggleDialog} style={{ color: '#170a33' }}>
              {t('importFCL:cancel')}
            </Button>
            <Button
              onClick={onAddButtonClick}
              className={styles.containersButton}
              disabled={
                !isSuccess ||
                addingContainer ||
                savingSettlement ||
                isLoading ||
                !containerData
              }
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              {t('importFCL:add')}
            </Button>
          </div>
        </div>
      }
    >
      {isLoading && (
        <div
          style={{ zIndex: 10, background: 'rgba(0,0,0, 0.2)' }}
          className="absolute top-0 bottom-0 left-0 right-0 z-10 flex align-items-center justify-content-center"
        >
          <LoadingSpinner />
        </div>
      )}
      <div className="p-4 md:pt-0">
        <div className={`${styles.formContainer} flex-column md:flex-row`}>
          <div
            className={`${styles.form} flex flex-column md:flex-row md:gap-3`}
          >
            <InputTextField
              id="contenedor"
              name="contenedor"
              value={formValue.contenedor}
              onChange={onContainerInputChange}
              label={t('importFCL:containerNumber')}
              className="w-full"
            />
            <InputDateField
              id="fechaIngreso"
              name="fechaIngreso"
              value={formValue.fechaIngreso}
              onChange={(e) => handleFieldChange(e, 'fechaIngreso')}
              label={t('exportFCL:withdrawalDateExport')}
              className="w-full"
            />
            {isLateArrival && (
              <CheckBox
                style={{ width: '100%' }}
                className="mt-3 md:mt-0"
                label={`${t('importFCL:lateEntry')} ${cutOffDate}`}
                checked={formValue.lateArrival}
                onChange={(checked) =>
                  handleFieldChange(checked, 'lateArrival')
                }
              />
            )}
          </div>
          <Button
            type="button"
            onClick={onCalculateButtonClick}
            className="w-full md:w-auto"
            style={{ padding: '0.5rem 1rem' }}
            disabled={
              !formValue.fechaIngreso ||
              !regexValidate.test(formValue.contenedor) ||
              calculatingAmount ||
              gettingContainerData ||
              !isValid
            }
          >
            <FontAwesomeIcon icon={faCalculator} className="mr-2" />
            {t('importFCL:calculateAmount')}
          </Button>
        </div>
        <div className="">
          {!!amountData && <InvoiceDetail data={amountData} />}
        </div>
      </div>
      <ModalDeleted
        visible={deleteModalIsVisible}
        onClose={onDeleteModalClose}
        oidVisita={containerData?.oidVisita || 0}
      />
    </Dialog>
  );
};

export default AddContainerModalExportFCL;
